import {createAsyncThunk, createSlice, current} from '@reduxjs/toolkit';
import {child, get, onValue, orderByChild, query, ref} from "firebase/database";
import { dbRef, RDB } from "../../auth/FirebaseContext";
import {
    assignPaymentPreprocesedApi,
    assignPaymentToUserApi,
    blockPaymentPreprocesedApi,
    deleteUserPaymentApi,
    findUsersForPaymentApi,
    newUploadXMLPaymentsApi,
    paymentNotToBeProcessedApi,
    resetPreprocessedPaymentToDefaultApi,
    uploadXMLPaymentsApi,
    deleteAllFromPreprocessedApi,
    processPaymentsFromPreprocessedApi
  } from '../../api'

export const fetchAllPayments = createAsyncThunk('fetchAllPayments', async () => {
    const dbRef = ref(RDB, `bss/finance/payments/processed`);
    let rowNum = 0;
    await onValue(dbRef, (snapshot => {
        rowNum = snapshot.size
    }))
    let q = query(dbRef, orderByChild("status"));
    return await new Promise(async (resolve) => {
        await onValue(q, async (snapshot) => {
            let arr = [];
            snapshot.forEach(child => {
                let parsedChild = child.val();
                if (parsedChild.status) {
                    if (parsedChild.status === 2) {
                        arr.unshift({
                            id: child.key,
                            ...parsedChild
                        })
                    } else {
                        arr.push({
                            id: child.key,
                            ...parsedChild
                        })
                    }
                }
            })
            resolve({
                numberOfRows: rowNum,
                data: arr
            })
        });
    });
})

export const fetchAllPreprocessedPayments = createAsyncThunk('fetchAllPreprocessedPayments', async () => {
    let preprocessedPayments = await (await get(child(dbRef, `bss/finance/payments/preprocessed`))).val();
    if (preprocessedPayments === null) {
        return {
            numberOfRows: 0,
            data: []
        }
    }
    let rowNum = Object.keys(preprocessedPayments).length;
    const dataWithIndex = Object.values(preprocessedPayments).map((payment, index) => ({
        ...payment,
        index
    }));
    return {
        numberOfRows: rowNum,
        data: dataWithIndex
    }
})

export const assignPaymentPreprocesed = createAsyncThunk(
    'assignPaymentPreprocesed',
    async (data, { rejectWithValue }) => {
      try {
        const { index, dataToSend } = data;
        const response = await assignPaymentPreprocesedApi(dataToSend);

        if (response.status !== 200) {
          return rejectWithValue(response);
        } else {
            const newPaymentData = await (await get(child(dbRef, `bss/finance/payments/preprocessed/${dataToSend.fitid}`))).val()
            return {
                data: {
                    index: index,
                    ...newPaymentData
                }
            }
        }
      } catch (error) {
        return rejectWithValue(error.response?.data || error.message || 'Unknown error');
      }
    }
  );

export const paymentNotToBeProcessed = createAsyncThunk('paymentNotToBeProcessed', async (data) => {
    const { dataToSend } = data;
    return await paymentNotToBeProcessedApi(dataToSend);
})

export const blockPaymentPreprocesed = createAsyncThunk('blockPaymentPreprocesed', async (data) => {
    const { dataToSend } = data;
    return await blockPaymentPreprocesedApi(dataToSend);
})

export const resetPreprocessedPaymentToDefault = createAsyncThunk('resetPreprocessedPaymentToDefault', async (data) => {
    const { dataToSend } = data;
    return await resetPreprocessedPaymentToDefaultApi(dataToSend);
})

export const fetchAllUsersForPayment = createAsyncThunk('fetchAllUsersForPayment', async (fitid) => {
    return findUsersForPaymentApi({fitid: fitid});
})

export const assignPaymentToUser = createAsyncThunk('assignPaymentToUser', async (data) => {
    return assignPaymentToUserApi(data);
})

export const deleteUserPayment = createAsyncThunk('deleteUserPayment', async (data) => {
    return deleteUserPaymentApi(data);
})

export const manualAssignPayment = createAsyncThunk('manualAssignPayment', async (data) => {
    return assignPaymentToUserApi(data);
})

export const uploadXMLPayments = createAsyncThunk('uploadXMLPayments',async (data, { rejectWithValue }) => {
    try {
      const response = await uploadXMLPaymentsApi(data);
      return response;
    } catch (error) {
        console.error(error);
      return rejectWithValue(error.response ? error.response.data : error.message);
    }
})

export const newUploadXMLPayments = createAsyncThunk('newUploadXMLPayments',async (data, { rejectWithValue }) => {
    try {
      const response = await newUploadXMLPaymentsApi(data);
      return response;
    } catch (error) {
        console.error(error);
      return rejectWithValue(error.response ? error.response.data : error.message);
    }
})

export const processPaymentsFromPreprocessed = createAsyncThunk('processPaymentsFromPreprocessed',async () => {
      return await processPaymentsFromPreprocessedApi();
})

export const deleteAllFromPreprocessed = createAsyncThunk('deleteAllFromPreprocessed',async () => {
    await deleteAllFromPreprocessedApi();
})

const initialState = {
    summary : null,
    payments: [],
    preprocessedPayments: [],
    loading: [],
    loadingXML: false,
    loadingPreprocessedChanges: "NO_CHANGES",
    loadingPreprocessedDelete : false,
    loadingProcessing: false,
}

export const slice = createSlice({
    name: 'payments',
    initialState,
    reducers: {
        resetSummary: state => {
            state.summary = null;
        },
        setXMLLoading: state => {
            state.loadingXML = true;
        }
    },
    extraReducers: {
        [fetchAllPayments.pending]: (state) => {
            state.loading = true
        },
        [fetchAllPayments.fulfilled]: (state, {payload}) => {
            state.payments = payload.data
            state.loading = false
        },
        [fetchAllPayments.rejected]: (state) => {
            state.loading = false
        },
        //fetchAllPreprocessedPayments
        [fetchAllPreprocessedPayments.pending]: state => {
            state.loading = true
        },
        [fetchAllPreprocessedPayments.fulfilled]: (state, { payload }) => {
            state.preprocessedPayments = payload.data
            state.loading = false
        },
        [fetchAllPreprocessedPayments.rejected]: state => {
            state.loading = false
        },
        //assignPaymentPreprocesed
        [assignPaymentPreprocesed.pending]: (state, { meta }) => {
            state.loadingPreprocessedChanges = meta.arg.dataToSend.fitid
        },
        [assignPaymentPreprocesed.fulfilled]: (state, { meta, payload }) => {
            const { index } = meta.arg
            const arr = [...current(state.preprocessedPayments)]

            if (arr[index]) {
            arr[index] = payload.data
            state.preprocessedPayments = arr
            } else {
                console.error(`Invalid index: ${index} in preprocessedPayments`)
            }

            state.loadingPreprocessedChanges = 'NO_CHANGES'
        },
        [assignPaymentPreprocesed.rejected]: state => {
            state.loadingPreprocessedChanges = 'NO_CHANGES'
        },
        //paymentNotToBeProcessed
        [paymentNotToBeProcessed.pending]: (state, { meta }) => {
            state.loadingPreprocessedChanges = meta.arg.dataToSend.fitid
        },
        [paymentNotToBeProcessed.fulfilled]: (state, { meta }) => {
            let index = meta.arg.index
            let arr = [...current(state.preprocessedPayments)]
            if (arr[index]) {
            const updatedItem = {
                ...arr[index],
                assigned: {},
                leftToAssign: arr[index].trnamt,
                status: 'Do not process',
                comment: undefined,
                assignedNames: {}
            }

            arr[index] = updatedItem
            state.preprocessedPayments = arr
            } else {
                console.error(`Invalid index: ${index} in preprocessedPayments`)
            }
            state.loadingPreprocessedChanges = 'NO_CHANGES'
        },
        [paymentNotToBeProcessed.rejected]: state => {
            state.loadingPreprocessedChanges = 'NO_CHANGES'
        },
        //blockPaymentPreprocesed
        [blockPaymentPreprocesed.pending]: (state, { meta }) => {
            state.loadingPreprocessedChanges = meta.arg.dataToSend.fitid
        },
        [blockPaymentPreprocesed.fulfilled]: (state, { meta }) => {
            let index = meta.arg.index
            let arr = [...current(state.preprocessedPayments)]
            if (arr[index]) {
            const updatedItem = {
                ...arr[index],
                assigned: {},
                leftToAssign: arr[index].trnamt,
                status: 'Blocked',
                comment: 'Manually blocked',
                assignedNames: {}
            }

            arr[index] = updatedItem
            state.preprocessedPayments = arr
            } else {
                console.error(`Invalid index: ${index} in preprocessedPayments`)
            }
            state.loadingPreprocessedChanges = 'NO_CHANGES'
        },
        [blockPaymentPreprocesed.rejected]: state => {
            state.loadingPreprocessedChanges = 'NO_CHANGES'
        },
        //resetPreprocessedPaymentToDefault
        [resetPreprocessedPaymentToDefault.pending]: (state, { meta }) => {
            state.loadingPreprocessedChanges = meta.arg.dataToSend.fitid
        },
        [resetPreprocessedPaymentToDefault.fulfilled]: (state, { meta }) => {
            let index = meta.arg.index
            let arr = [...current(state.preprocessedPayments)]
            if (arr[index]) {
            const updatedItem = {
                ...arr[index],
                assigned: arr[index].default.assigned,
                leftToAssign: arr[index].default.leftToAssign,
                status: arr[index].default.status,
                comment: undefined,
                assignedNames: arr[index].default.assignedNames
            }

            arr[index] = updatedItem
            state.preprocessedPayments = arr
            } else {
                console.error(`Invalid index: ${index} in preprocessedPayments`)
            }
            state.loadingPreprocessedChanges = 'NO_CHANGES'
        },
        [resetPreprocessedPaymentToDefault.rejected]: state => {
            state.loadingPreprocessedChanges = 'NO_CHANGES'
        },
        //deleteAllFromPreprocessed
        [deleteAllFromPreprocessed.pending]: state => {
            state.loadingPreprocessedDelete = true
        },
        [deleteAllFromPreprocessed.fulfilled]: state => {
            state.preprocessedPayments = []
            state.loadingPreprocessedDelete = false
        },
        [deleteAllFromPreprocessed.rejected]: state => {
            state.loadingPreprocessedDelete = false
        },
        [uploadXMLPayments.pending]: (state) => {
            state.loadingXML = true
        },
        [uploadXMLPayments.fulfilled]: (state, {payload}) => {
            const newSummary = {
                count: payload.data.count,
                processed: payload.data.processed,
                notProcessed: payload.data.notProcessed,
                suspicious: payload.data.suspicious,
                existing: payload.data.existing
            }
            state.summary = newSummary
            state.loadingXML = false
        },
        //newUploadXMLPayments
        [newUploadXMLPayments.pending]: (state) => {
            state.loadingXML = true
        },
        [newUploadXMLPayments.fulfilled]: (state) => {
            state.loadingXML = false
        },
        [newUploadXMLPayments.rejected]: (state) => {
            state.loadingXML = false
        },
        //processPaymentsFromPreprocessed
        [processPaymentsFromPreprocessed.pending]: (state) => {
            state.loadingProcessing = true
        },
        [processPaymentsFromPreprocessed.fulfilled]: (state, {payload}) => {
            const newSummary = {
                count: payload.data.count,
                processed: payload.data.processed,
                notProcessed: payload.data.notProcessed,
                blocked: payload.data.blocked,
                notLoaded: payload.data.notLoaded,
                existing: payload.data.existing
            }
            state.summary = newSummary
            state.loadingProcessing = false
        },
        [processPaymentsFromPreprocessed.rejected]: (state) => {
            state.loadingProcessing = false
        },
        [deleteUserPayment.fulfilled]: (state, {meta}) => {
            let arr = [...current(state.payments)]
            let index = arr.findIndex((payment) => payment.id === meta.arg.fitid)
            arr.splice(index, 1)
            state.payments = arr;
        },
        [manualAssignPayment.fulfilled]: (state, {meta}) =>{
            let arr = [...current(state.payments)]
            let index = arr.findIndex((payment) => payment.id === meta.arg.fitid)
            if(index !== -1){
                arr[index] = {
                    ...arr[index],
                    trnamt: Number(Number(arr[index].trnamt) - Number(meta.arg.amount))
                }
            }
        }
    }
});
export const {resetSummary, setXMLLoading} = slice.actions
export default slice.reducer;


